import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import AdminProductEdit from './AdminProductEdit';
import AdminProductAdd from './AdminProductAdd';
import Button from './ui/Button';
import '../styles/AdminProductList.css';
import Toast from './Toast';
import getImgSrc from '../utils/getImgSrc';

const AdminProductList = () => {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [editingProductId, setEditingProductId] = useState(null);
	const [addingProduct, setAddingProduct] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [toast, setToast] = useState('');

	const fetchProducts = async () => {
		try {
			const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/products`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			setProducts(response.data);
			setLoading(false);
		} catch (err) {
			setError('Błąd podczas ładowania produktów');
			setToast('Nie udało się pobrać produktów.');
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProducts();
	}, []);

	const handleDelete = async (id) => {
		const confirmDelete = window.confirm('Czy na pewno chcesz usunąć ten produkt?');
		if (!confirmDelete) return;
		try {
			await axios.delete(`${process.env.REACT_APP_API_URL}/api/products/${id}`, {
				headers: {
					Authorization: `Bearer ${localStorage.getItem("authToken")}`,
				},
			});
			setProducts((prev) => prev.filter((product) => product._id !== id));
			setToast('Produkt został usunięty.');
		} catch (err) {
			setToast('Nie udało się usunąć produktu.');
		}
	};

	const handleEdit = (id) => {
		setAddingProduct(false);
		setEditingProductId(id);
	};

	const handleCloseEdit = () => {
		setEditingProductId(null);
	};

	const handleProductUpdated = () => {
		fetchProducts();
		setEditingProductId(null);
	};

	const handleProductAdded = () => {
		fetchProducts();
		setAddingProduct(false);
	};

	const filteredProducts = useMemo(() => {
        return products
            .filter((product) =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.internalCode.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .reverse();
    }, [products, searchTerm]);

	if (loading) return <div>Ładowanie...</div>;
	if (error) return <div>{error}</div>;

	return (
		<div className="existing-products-wrapper">
			<h3>Lista produktów</h3>

			<input
				type="text"
				placeholder="Wyszukaj po nazwie lub kodzie towaru..."
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				className="search-input"
			/>

			<Button variant="primary" size="small" onClick={() => { setEditingProductId(null); setAddingProduct(true); }}>
				➕ Dodaj nowy produkt
			</Button>

			{addingProduct && (
				<AdminProductAdd
					closeForm={() => setAddingProduct(false)}
					onProductAdded={handleProductAdded}
				/>
			)}

			{editingProductId && !addingProduct && (
				<AdminProductEdit
					productId={editingProductId}
					closeForm={handleCloseEdit}
					onProductUpdated={handleProductUpdated}
				/>
			)}

			{!addingProduct && !editingProductId && (
				<div className="table-responsive">
					<table className="existing-products-table">
						<thead>
							<tr>
								<th>Zdjęcie</th>
								<th>ID</th>
								<th>Nazwa</th>
								<th>Kod towaru</th>
								<th>Cena</th>
								<th>Akcje</th>
							</tr>
						</thead>
						<tbody>
							{filteredProducts.length > 0 ? (
								filteredProducts.map((product) => (
									<tr key={product._id}>
										<td>
											{product.images?.length > 0 ? (
												<img
													src={getImgSrc(product.images[0])}
													alt={product.name}
													className="product-thumb"
												/>
											) : (
												<p>Brak zdjęcia</p>
											)}
										</td>
										<td>{product._id}</td>
										<td>{product.name}</td>
										<td>{product.internalCode}</td>
										<td>{product.price.toFixed(2)}</td>
										<td className="action-buttons">
										<Button variant="primary" size="small" onClick={() => handleEdit(product._id)}>Edytuj</Button>
										<Button variant="danger" size="small" onClick={() => handleDelete(product._id)}>Usuń</Button>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan="6">Brak produktów spełniających kryteria wyszukiwania</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>)}

			{toast && <Toast message={toast} onClose={() => setToast('')} />}
		</div>
	);
};

export default AdminProductList;
